import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecipients } from 'xmpl-react';
import { API_URL, ACCESS_TOKEN } from '../config';
import ProductCheckboxes from '../components/ProductCheckboxes';

/**
 * RegistrationPage — anonymous self-registration form.
 *
 * Collects personal details and product subscription preferences, then
 * creates a new Circle recipient via the XMPL `addRecipient` hook.
 * On success it fires the E2 welcome-email touchpoint and redirects
 * to the /success confirmation page.
 */
export default function RegistrationPage() {
  const navigate = useNavigate();
  const { addRecipient } = useRecipients();

  /**
   * Trigger the Circle welcome-email touchpoint (E2) for a newly
   * registered recipient.
   * @param {string} recipientID - The Circle recipient ID returned by addRecipient.
   */
  const triggerEmail = async (recipientID) => {
    const response = await fetch(`${API_URL}/v1/projects/${ACCESS_TOKEN}/touchpoints/trigger`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        recipientID,
        touchPointID: 'E2',
      }),
    });
    if (!response.ok) {
      throw new Error(`Trigger API returned ${response.status}`);
    }
    return response.json();
  };

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('');
  const [subscribeAll, setSubscribeAll] = useState(false);
  const [subscriptions, setSubscriptions] = useState({
    Sub_uCreatePrint: false,
    Sub_uProduce: false,
    Sub_uStore: false,
    Sub_Circle: false,
    Sub_uPlan: false,
    Sub_XMPieVideoService: false,
    Sub_XMPieEmailService: false,
    Sub_uImage: false,
  });
  const [consentGiven, setConsentGiven] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const validate = () => {
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      return 'First Name, Last Name, and Email are required.';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return 'Please enter a valid email address.';
    }
    if (!consentGiven) {
      return 'You must consent to receive security communications.';
    }
    const hasProduct = subscribeAll || Object.values(subscriptions).some(v => v);
    if (!hasProduct) {
      return 'Please select at least one product to subscribe to.';
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');
    setSubmitting(true);

    const today = new Date().toISOString().split('T')[0];

    try {
      const result = await addRecipient({
        isAddReferFriend: false,
        adors: {
          FirstName: firstName,
          LastName: lastName,
          Email: email,
          Company: company,
          JobTitle: jobTitle,
          Phone: phone,
          Country: country,
          SubscribeAll: subscribeAll ? 'TRUE' : 'FALSE',
          Sub_uCreatePrint: subscriptions.Sub_uCreatePrint ? 'TRUE' : 'FALSE',
          Sub_uProduce: subscriptions.Sub_uProduce ? 'TRUE' : 'FALSE',
          Sub_uStore: subscriptions.Sub_uStore ? 'TRUE' : 'FALSE',
          Sub_Circle: subscriptions.Sub_Circle ? 'TRUE' : 'FALSE',
          Sub_uPlan: subscriptions.Sub_uPlan ? 'TRUE' : 'FALSE',
          Sub_XMPieVideoService: subscriptions.Sub_XMPieVideoService ? 'TRUE' : 'FALSE',
          Sub_XMPieEmailService: subscriptions.Sub_XMPieEmailService ? 'TRUE' : 'FALSE',
          Sub_uImage: subscriptions.Sub_uImage ? 'TRUE' : 'FALSE',
          ConsentGiven: 'TRUE',
          DateRegistered: today,
          LastUpdated: today,
        },
      });

      if (result) {
        try {
          const rid = result.recipientID || localStorage.getItem('xmpRecipientID');
          if (rid) {
            await triggerEmail(rid);
          } else {
            console.warn('No recipientID available to trigger welcome email');
          }
        } catch (triggerErr) {
          console.error('Failed to send welcome email:', triggerErr);
        }
        navigate('/success');
      } else {
        setError('Registration failed. Please try again or contact support.');
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-hero">
        <h1>XMPie Security Compliance</h1>
        <p>Register to receive security updates and compliance notifications for XMPie products.</p>
      </div>

      <div className="form-card">
        <h2>Create Your Account</h2>
        <p className="form-subtitle">
          Complete the form below to subscribe to security notifications for the XMPie products you use.
        </p>

        {error && <div className="form-error">{error}</div>}

        <form onSubmit={handleSubmit} noValidate>
          <div className="form-section">
            <h3>Personal Information</h3>
            <div className="form-grid">
              <div className="form-field">
                <label htmlFor="firstName">First Name <span className="required">*</span></label>
                <input
                  type="text"
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Enter your first name"
                  required
                />
              </div>
              <div className="form-field">
                <label htmlFor="lastName">Last Name <span className="required">*</span></label>
                <input
                  type="text"
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Enter your last name"
                  required
                />
              </div>
              <div className="form-field form-field--full">
                <label htmlFor="email">Email Address <span className="required">*</span></label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                />
              </div>
              <div className="form-field">
                <label htmlFor="company">Company</label>
                <input
                  type="text"
                  id="company"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="Your organization"
                />
              </div>
              <div className="form-field">
                <label htmlFor="jobTitle">Job Title</label>
                <input
                  type="text"
                  id="jobTitle"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  placeholder="Your role"
                />
              </div>
              <div className="form-field">
                <label htmlFor="phone">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1-555-000-0000"
                />
              </div>
              <div className="form-field">
                <label htmlFor="country">Country</label>
                <input
                  type="text"
                  id="country"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  placeholder="Your country"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Product Subscriptions</h3>
            <p className="form-hint">Select the products for which you would like to receive security updates.</p>
            <ProductCheckboxes
              subscriptions={subscriptions}
              onChange={setSubscriptions}
              subscribeAll={subscribeAll}
              onSubscribeAllChange={setSubscribeAll}
            />
          </div>

          <div className="form-section">
            <div className="consent-row">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={consentGiven}
                  onChange={(e) => setConsentGiven(e.target.checked)}
                />
                <span className="checkbox-custom" />
                <span className="checkbox-text">
                  I consent to receive security and compliance communications from XMPie regarding the selected products. <span className="required">*</span>
                </span>
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Registering...' : 'Register for Security Updates'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
